import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { roleSchema } from "../../schemas/roleSchema";
import LoadingTransition from "../../components/ui/LoadingTransition";
import RoleSkeleton from "../../components/roles/RoleSkeleton";
import PermissionMatrix from "../../components/roles/PermissionMatrix";
import { Users } from "lucide-react";
import LoadingButton from "../../components/LoadingButton";
import React, { useMemo } from "react";

function RoleManagementForm({
  mode,
  defaultValues = {},
  onSubmit,
  onCancel,
  submissionLoading = false,
  isModalLoading,
}) {
  // Use modalMode and selectedRole from props or defaultValues
  const modalMode = mode;

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(roleSchema),
    defaultValues,
    mode: "onBlur",
  });

  const memoizedPermissions = useMemo(() => {
    return defaultValues?.permissions || [];
  }, [defaultValues?.permissions]);

  return (
    <form
      className="overflow-auto flex-1 shrink-0 m-3 p-2"
      onSubmit={handleSubmit(onSubmit)}
    >
      <LoadingTransition
        isLoading={isModalLoading}
        skeleton={<RoleSkeleton.Modal />}
        transition={{ duration: 0.3 }}
      >
        <div className="space-y-4 relative">
          {/* Role Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role Name
            </label>
            <input
              type="text"
              className={`px-3 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                errors.name ? "border-red-500" : ""
              }`}
              {...register("name")}
              readOnly={
                modalMode === "view" || defaultValues?.name === "Super Admin"
              }
              placeholder="E.g., Content Manager"
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              className={`px-3 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                errors.description ? "border-red-500" : ""
              }`}
              {...register("description")}
              readOnly={modalMode === "view"}
              placeholder="Describe the role's purpose and responsibilities"
              rows={3}
            />
            {errors.description && (
              <p className="text-red-500 text-sm mt-1">
                {errors.description.message}
              </p>
            )}
          </div>

          {/* Permissions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Permissions
            </label>
            <div className="border rounded-md p-4 overflow-auto relative h-100">
              <LoadingTransition
                isLoading={isModalLoading}
                skeleton={<RoleSkeleton.PermissionMatrix />}
                transition={{ duration: 0.2 }}
              >
                <PermissionMatrix
                  permissions={memoizedPermissions}
                  readOnly={modalMode === "view"}
                  isSystemRole={defaultValues?.name === "Super Admin"}
                  onChange={(newPermissions) => {
                    setValue("permissions", newPermissions);
                  }}
                />
              </LoadingTransition>
            </div>
            {errors.permissions && (
              <p className="text-red-500 text-sm mt-1">
                {errors.permissions.message}
              </p>
            )}
          </div>

          {/* Users Assigned */}
          {defaultValues && modalMode === "view" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Users Assigned
              </label>
              <div className="flex items-center py-2">
                <Users className="h-5 w-5 text-gray-400 mr-2" />
                <span className="text-gray-700">
                  {defaultValues.users_count}{" "}
                  {defaultValues.users_count > 1 ? "users" : "user"}
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t flex justify-end space-x-3">
          <button
            type="button"
            className="px-4 py-2 border rounded-md text-gray-700 bg-white hover:bg-gray-50"
            onClick={onCancel}
          >
            {modalMode === "view" ? "Close" : "Cancel"}
          </button>

          {mode !== "view" && (
            <button
              type="submit"
              disabled={submissionLoading}
              className="px-4 py-2 bg-primary text-white rounded flex items-center justify-center gap-2"
            >
              {submissionLoading ? (
                <LoadingButton />
              ) : (
                <>{mode === "edit" ? "Save Changes" : "Create Role"}</>
              )}
            </button>
          )}
        </div>
      </LoadingTransition>
    </form>
  );
}

export default RoleManagementForm;
